import { Routes, Route, Navigate } from "react-router-dom";

import Page404 from "./pages/Page404.js";
import Home from "./pages/Home/Home.js";
import Privacy from "./pages/Privacy/Privacy.js";
import Mission from "./pages/Mission/Mission";
import Platforms from "./pages/Platforms/Platforms";
import Community from "./pages/Community/Community";
import Layout from "./layout/Layout.js";
import SiteLayout from "./layout/SiteLayout.js";
import RequireAuth from "./components/login/RequireAuth.js";

import LoginPage from "./app/auth/LoginPage";
import SignupPage from "./app/auth/SignupPage";
import EmailVerifyPage from "./app/auth/EmailVerifyPage";
import ForgotPasswordPage from "./app/auth/ForgotPasswordPage";
import RequireUser from "./components/login/RequireUser";
import AppShell from "./app/shell/AppShell";
import ProfilePage from "./app/profile/ProfilePage";
import AccountPage from "./app/profile/AccountPage";
import AccountProfilePage from "./app/profile/AccountProfilePage";
import AccountPasswordPage from "./app/profile/AccountPasswordPage";
import ResultsPage from "./app/results/ResultsPage";
import StatisticsPage from "./app/statistics/StatisticsPage";
import GroupListPage from "./app/groups/GroupListPage";
import GroupDetailPage from "./app/groups/GroupDetailPage";
import MemberGroupListPage from "./app/groups/MemberGroupListPage";
import ManagedGroupListPage from "./app/management/ManagedGroupListPage";
import ManagedGroupDetailPage from "./app/management/ManagedGroupDetailPage";
import ManagedGroupStatsPage from "./app/management/ManagedGroupStatsPage";
import HatimPage from "./app/hatim/HatimPage";
import HatimTemplateListPage from "./app/hatimManagement/HatimTemplateListPage";
import NotificationsPage from "./app/notifications/NotificationsPage";
import TitlesPage from "./app/titles/TitlesPage";
import AssessmentPage from "./app/muzakere/AssessmentPage";
import LibraryPage from "./app/library/LibraryPage";
import PackageDetailPage from "./app/library/PackageDetailPage";
import PackageEditPage from "./app/library/PackageEditPage";
import ProposalPage from "./app/library/ProposalPage";
import AccessRequestsPage from "./app/library/AccessRequestsPage";
import SystemDashboardPage from "./app/admin/SystemDashboardPage";
import LibraryReviewPage from "./app/admin/LibraryReviewPage";
import AdminUsersPage from "./app/admin/AdminUsersPage";
import AdminAnnouncementsPage from "./app/admin/AdminAnnouncementsPage";
import AuditLogPage from "./app/admin/AuditLogPage";

const ROLES = {
  Admin: "Admin",
  Accountant: "Accountant",
};

function App() {
  return (
    <Routes>
      {/* Public landing — unchanged */}
      <Route path="/" element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="misyonumuz" element={<Mission />} />
        <Route path="mobil-ve-web" element={<Platforms />} />
        <Route path="topluluk" element={<Community />} />
        <Route path="gizlilik-sozlesmesi" element={<Privacy />} />
      </Route>

      {/* User-facing auth flow */}
      <Route path="/giris" element={<LoginPage />} />
      <Route path="/kayit" element={<SignupPage />} />
      <Route path="/email-dogrula" element={<EmailVerifyPage />} />
      <Route path="/sifremi-unuttum" element={<ForgotPasswordPage />} />

      {/* Protected user app */}
      <Route element={<RequireUser />}>
        <Route path="/uygulama" element={<AppShell />}>
          <Route index element={<Navigate to="profil" replace />} />
          <Route path="profil" element={<ProfilePage />} />
          <Route path="hesap" element={<AccountPage />} />
          {/* Hesap ayarlarının alt sayfaları: profil bilgileri ve şifre */}
          <Route path="hesap/profil" element={<AccountProfilePage />} />
          <Route path="hesap/sifre" element={<AccountPasswordPage />} />
          {/* Hedefler artık Terakkim'deki kalemle açılan çekmecede */}
          <Route path="hedefler" element={<Navigate to="/uygulama/profil" replace />} />
          <Route path="sonuclar" element={<ResultsPage />} />
          <Route path="istatistik" element={<StatisticsPage />} />
          <Route path="gruplar" element={<MemberGroupListPage />} />
          <Route path="gruplar/:groupId" element={<GroupDetailPage />} />
          <Route path="gruplarim" element={<GroupListPage />} />
          <Route path="gruplarim/:groupId" element={<GroupDetailPage />} />
          <Route path="yonetim" element={<ManagedGroupListPage />} />
          <Route path="yonetim/:groupId" element={<ManagedGroupDetailPage />} />
          <Route path="yonetim/:groupId/istatistikler" element={<ManagedGroupStatsPage />} />
          <Route path="hatim" element={<HatimPage />} />
          <Route path="hatim-yonetim" element={<HatimTemplateListPage />} />
          <Route path="hatim-yonetim/:templateId" element={<HatimTemplateListPage />} />
          <Route path="duyurular" element={<NotificationsPage />} />
          <Route path="unvanlar" element={<TitlesPage />} />
          {/* Kaza Namazlarım artık Terakkim'de açılan çekmecede */}
          <Route path="kaza" element={<Navigate to="/uygulama/profil" replace />} />
          <Route path="muzakere/:id/degerlendirme" element={<AssessmentPage />} />
          {/* Müzakere kütüphanesi: liste, paket, oluştur/düzenle, öneri, izin istekleri */}
          <Route path="kutuphane">
            <Route index element={<LibraryPage />} />
            <Route path="yeni" element={<PackageEditPage />} />
            <Route path="istekler" element={<AccessRequestsPage />} />
            <Route path="oneri/:id" element={<ProposalPage />} />
            <Route path=":id" element={<PackageDetailPage />} />
            <Route path=":id/duzenle" element={<PackageEditPage />} />
          </Route>
        </Route>

        {/* Terakki Yönetimi: yalnız Admin (AppShell admin kipi rolü denetler) */}
        <Route path="/sistem" element={<AppShell variant="admin" />}>
          <Route index element={<Navigate to="pano" replace />} />
          <Route path="pano" element={<SystemDashboardPage />} />
          <Route path="kutuphane" element={<LibraryReviewPage />} />
          <Route path="kutuphane/:packageId" element={<LibraryReviewPage />} />
          <Route path="kullanicilar" element={<AdminUsersPage />} />
          <Route path="duyurular" element={<AdminAnnouncementsPage />} />
          <Route path="kayitlar" element={<AuditLogPage />} />
          <Route path="*" element={<Navigate to="pano" replace />} />
        </Route>
      </Route>

      {/* Existing admin scaffold — unchanged */}
      <Route element={<RequireAuth allowedRoles={[ROLES.Admin, ROLES.Accountant]} />}>
        <Route path="" element={<SiteLayout />}>
          <Route path="*" element={<Page404 />} />
        </Route>
      </Route>

      <Route path="*" element={<Page404 />} />
    </Routes>
  );
}

export default App;
